<script setup lang="ts">
import BaseMediaItemList from "@/components/Base/MediaItemList.vue";
import HomeMoreLink from "@/components/home/MoreLink.vue";
import StartpageAssistantItem from "@/components/startpage/AssistantItem.vue";
import StartpageLinkTile from "@/components/startpage/LinkTile.vue";
import StartpageMiniToolItem from "@/components/startpage/MiniToolItem.vue";
import StartpageSectionHeadline from "@/components/startpage/SectionHeadline.vue";
import { useAssistantQueries } from "@/composables/ai-chat/useAssistantQueries";
import { useMiniToolsQueries } from "@/composables/ai-mini-tools/useMiniToolsQueries";
import type { AiAssistant } from "@/services/fobizzToolsApi";
import { RouteLocationRaw } from "vue-router";

const { t } = useI18n();
const router = useRouter();
const userStore = useUserStore();
const route = useRoute();

const { useFeaturedQuery } = useMiniToolsQueries();
const international = ref(
  (route.query.international as string) ||
    (userStore.user?.international &&
      userStore.user.international.toString()) ||
    "false"
);
const { data: miniTools } = useFeaturedQuery({
  limit: 3,
  international,
});

const { useFeaturedQuery: useFeaturedAssistantQuery } = useAssistantQueries();

const { data: assistants } = useFeaturedAssistantQuery({
  limit: 3,
  international: international,
});

const handleAssistantClick = (assistant: AiAssistant) => {
  createConversation(assistant);
};

const { useCreateMutation: useCreateConversationMutation } =
  useConversationQueries();

const createConversation = async (assistant: AiAssistant, starter?: string) => {
  const { mutateAsync } = useCreateConversationMutation();
  const data = await mutateAsync({
    name: t("aiChat.labels.newConversation"),
    aiAssistantId: assistant.id,
  });

  const options: RouteLocationRaw = {
    name: "ai_chat-show",
    params: { id: data.id },
  };

  if (starter) {
    options.query = {
      starterIndex: assistant.conversationStarters.indexOf(starter),
    };
  }

  router.push(options);
};

const teaserAttrs = computed(() => {
  return {
    title: t("startPage.teaser.title"),
    content: t("startPage.teaser.content"),
    linkName: t("startPage.teaser.link"),
    icon: "mic",
  };
});
</script>

<template>
  <div class="mb-6">
    <div class="featured-grid">
      <div class="grid-column">
        <StartpageSectionHeadline>
          {{ t("startPage.headers.teasers") }}
        </StartpageSectionHeadline>
        <div class="grid-column__body">
          <StartpageLinkTile
            v-bind="teaserAttrs"
            :to="{
              name: 'converters-index',
            }"
          />
        </div>
        <div class="grid-column__footer d-none d-lg-flex">
          <div class="btn btn-link font-sm">&nbsp;</div>
        </div>
      </div>
      <div class="grid-column">
        <StartpageSectionHeadline
          :badge="t('startPage.headers.miniToolsBadge')"
        >
          {{ t("startPage.headers.miniTools") }}
        </StartpageSectionHeadline>
        <div class="grid-column__body">
          <BaseMediaItemList
            v-if="miniTools && miniTools.collection.length > 0"
          >
            <StartpageMiniToolItem
              v-for="miniTool in miniTools.collection"
              :key="miniTool.id"
              :mini-tool="miniTool"
              hide-badge
            />
          </BaseMediaItemList>
        </div>
        <div class="grid-column__footer">
          <HomeMoreLink
            to="/ai/mini_tools"
            :title="t('startPage.links.miniToolsCatalog')"
          />
        </div>
      </div>
      <div class="grid-column">
        <StartpageSectionHeadline
          :badge="t('startPage.headers.assistantsBadge')"
        >
          {{ t("startPage.headers.assistants") }}
        </StartpageSectionHeadline>
        <div class="grid-column__body">
          <BaseMediaItemList v-if="assistants">
            <StartpageAssistantItem
              v-for="assistant in assistants.collection"
              :key="assistant.id"
              :assistant="assistant"
              hide-badge
              @click="handleAssistantClick(assistant)"
            />
          </BaseMediaItemList>
        </div>
        <div class="grid-column__footer">
          <HomeMoreLink
            to="/ai/chats/assistants"
            :title="t('startPage.links.assistantsCatalog')"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.featured-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-auto-rows: 1fr;
  gap: $spacer-3;

  @include media-breakpoint-down(xl) {
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: auto;
    row-gap: $spacer-6;
  }

  @include media-breakpoint-down(lg) {
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
    gap: $spacer-2;
    row-gap: $spacer-6;
  }
}

.grid-column {
  display: flex;
  flex-direction: column;
  min-width: 0;

  &__body {
    flex: 1;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    margin-top: $spacer-2;
  }
}
</style>
